import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

export default function ForgotPassword() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) { toast.error('Enter your email'); return; }
    setLoading(true);
    try {
      const res = await axios.post('https://budget-app-server-two.vercel.app/api/auth/forgot-password', { email });
      if (res.data.success) {
        setMaskedPhone(res.data.phone || 'your registered phone');
        toast.success('OTP sent to your phone!');
        setStep('otp');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const res = await axios.post('https://budget-app-server-two.vercel.app/api/auth/resend-otp', { email });
      if (res.data.success) toast.success('New OTP sent!');
    } catch (e) { toast.error('Failed to resend'); }
    finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { toast.error('OTP must be 6 digits'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      toast.error('Password needs uppercase, lowercase, and number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post('https://budget-app-server-two.vercel.app/api/auth/verify-otp-reset', { email, otp, newPassword });
      if (res.data.success) {
        toast.success('Password reset successfully!');
        setStep('success');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Reset failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-extrabold text-white">FinovaTrack</h2>
          <p className="mt-2 text-lg text-indigo-100">
            {step === 'email' && 'Reset Your Password'}
            {step === 'otp' && 'Enter OTP from SMS'}
            {step === 'success' && 'Password Reset!'}
          </p>
        </div>
        
        <div className="bg-white py-8 px-6 shadow-2xl rounded-2xl sm:px-10">
          {step === 'email' && (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" placeholder="you@example.com" />
                <p className="mt-2 text-xs text-gray-500">We'll send a 6-digit OTP to the phone number registered with your account.</p>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                {loading ? 'Sending...' : 'Send OTP via SMS'}
              </button>
              <p className="text-center text-sm text-gray-600">
                Remember password? <Link to="/login" className="text-indigo-600 font-medium">Sign in</Link>
              </p>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <p className="text-sm text-indigo-900">
                  📱 OTP sent to <strong>{maskedPhone}</strong>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Enter 6-digit OTP</label>
                <input type="text" inputMode="numeric" maxLength="6" required value={otp} 
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-center text-2xl tracking-widest font-mono" 
                  placeholder="000000" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <input type="password" required value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" 
                  placeholder="Min 8 chars, uppercase, number" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirm Password</label>
                <input type="password" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500" 
                  placeholder="Re-enter password" />
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium">
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
              <div className="text-center text-sm">
                <button type="button" onClick={handleResendOTP} disabled={loading} className="text-indigo-600 hover:text-indigo-500">
                  Didn't receive OTP? Resend
                </button>
              </div>
            </form>
          )}

          {step === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Password Reset!</h3>
              <p className="text-sm text-gray-600 mb-6">You can now sign in with your new password.</p>
              <Link to="/login" className="w-full inline-block py-3 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">
                Sign In Now
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}